import { api } from '@/lib/api';
import type { ApiResponse } from '@/types';

// Listas completas para selectores y lookups. Antes los hooks pedían `per_page`
// fijo (100/200) y usaban esa primera página como si fuera la colección entera:
// al crecer, los registros sobrantes desaparecían sin aviso (bug 2026-10-19,
// mismo algoritmo que grupo-dim-admin). O se devuelve todo, o se falla.

/** Igual a `PAGINATION.MAX_PER_PAGE` del API: menos peticiones por colección. */
export const ALL_PAGES_PER_PAGE = 500;
/** Tope defensivo de páginas por lectura. Superarlo es un error, nunca un truncado. */
export const ALL_PAGES_MAX_PAGES = 50;
/** Lecturas completas a intentar si la colección cambia mientras se pagina. */
export const ALL_PAGES_ATTEMPTS = 3;

type QueryParams = Record<string, string | number | undefined>;
export type AllPagesOptions = {
  /** Endpoints con `per_page` máximo menor al general (p. ej. acciones correctivas: 100). */
  pageSize?: number;
};

type Sweep<T> = { rows: T[]; total?: number; consistent: boolean };

// Una lectura de punta a punta. Con meta: termina en total_pages o en página vacía,
// y es consistente si todas las páginas reportaron el mismo total y se juntaron
// al menos esas filas. Sin meta: termina en una página corta o en una que no
// aporta filas nuevas (endpoint que ignora `page`).
async function sweep<T extends { id: number }>(
  path: string,
  params: QueryParams | undefined,
  pageSize: number,
): Promise<Sweep<T>> {
  const byId = new Map<number, T>();
  let firstTotal: number | undefined;
  let stableTotal = true;

  for (let page = 1; page <= ALL_PAGES_MAX_PAGES; page++) {
    const res = await api.get<T[]>(path, { ...params, page, per_page: pageSize });
    const before = byId.size;
    // Dedupe por id: un alta entre páginas desplaza el offset y repite filas.
    for (const row of res.data) byId.set(row.id, row);

    const pagination = res.meta?.pagination;
    if (!pagination) {
      if (res.data.length < pageSize || byId.size === before) {
        return { rows: [...byId.values()], consistent: true };
      }
      continue;
    }
    firstTotal ??= pagination.total;
    if (pagination.total !== firstTotal) stableTotal = false;
    if (page >= pagination.total_pages || res.data.length === 0) {
      const rows = [...byId.values()];
      return {
        rows,
        total: pagination.total,
        consistent: stableTotal && rows.length >= pagination.total,
      };
    }
  }

  throw new Error(`${path}: la colección supera ${ALL_PAGES_MAX_PAGES} páginas de ${pageSize}`);
}

export async function fetchAllPages<T extends { id: number }>(
  path: string,
  params?: QueryParams,
  options: AllPagesOptions = {},
): Promise<ApiResponse<T[]>> {
  const pageSize = options.pageSize ?? ALL_PAGES_PER_PAGE;
  let last: Sweep<T> | undefined;

  // Una baja entre páginas corre el offset y deja una fila sin leer sin que el
  // dedupe lo note; se detecta por total cambiante o filas de menos y se relee.
  for (let attempt = 1; attempt <= ALL_PAGES_ATTEMPTS; attempt++) {
    last = await sweep<T>(path, params, pageSize);
    if (last.consistent) {
      const data = last.rows;
      return {
        success: true,
        data,
        meta: {
          pagination: { page: 1, per_page: data.length, total: data.length, total_pages: 1 },
        },
      };
    }
  }

  throw new Error(
    `${path}: la colección cambió durante la lectura o llegó incompleta ` +
      `(${last?.rows.length ?? 0} de ${last?.total ?? '?'} tras ${ALL_PAGES_ATTEMPTS} intentos)`,
  );
}
